/**
 * OnboardingFlow.jsx — s95 sign-up rebuild (Dr. Rami, 2026-10-04).
 *
 * SIGN-UP IS THREE REQUIRED STEPS, then a choice:
 *   (Phone — only an Apple/Google account with no verified mobile)
 *   0 Security    two-factor + quick-access PIN (grants the session for a new account)
 *   1 About you   optional ID scan, then name / DOB 18+ / sex / address (all required)
 *   2 Agreements  AI use + Terms (Terms recorded on the server)
 *   → finish: identification + onboardingCompleted are written (buildEssentialsPayload)
 *   → celebration "Welcome to Orella, {name}" with two choices:
 *        Start my first case            → How Orella works (4) → case start
 *        Set up my health profile first → Insurance · Records · Allergies · Medications
 *                                          · More (each saved complete as it is done)
 *                                          → How Orella works → case start
 * Insurance, allergies and medications are required before a case is SHARED, not here.
 *
 * Retired: the 7-slide carousel (replaced by the post-sign-up tour), the Welcome basics
 * screen (folded into About you), and the finish that stamped every section complete.
 * Insurance discovery now starts on the server the moment identification is complete
 * (PUT /profile, API v243.273.0+), so the finish no longer searches.
 */
import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion'
import { screenVariants, reducedVariants } from '../../config/transitions'
import { Sparkles } from 'lucide-react'
import { useUserStore, useProfileStore, useOnboardingStore } from '../../stores'
import { profileAPI } from '../../services/api'
import { useI18n } from '../../i18n'
import { dobRefusalKey, ACCOUNT_HOLDER_MIN_AGE } from '../../utils/age'
import { hasCarrierAndMemberId, policyholderPending } from '../../utils/discoverySearch'
import { eligibilityKey, eligibilityAnswer, storeEligibilityAnswer } from '../../utils/eligibilityLimit'
import { markProfileRunPending, clearProfileRunPending } from '../../utils/profileRunPending'
import { consumeProfileRunAtFirstHome } from '../../utils/firstHome'
import OnboardingPhone from './OnboardingPhone'
import OnboardingSecurity from './OnboardingSecurity'
import OnboardingAIConsent from './OnboardingAIConsent'
import OnboardingAutoFill from './OnboardingAutoFill'
import OnboardingIdentity from './OnboardingIdentity'
import OnboardingInsurance from './OnboardingInsurance'
import OnboardingAllergies from './OnboardingAllergies'
import OnboardingMedications from './OnboardingMedications'
import OnboardingOptional from './OnboardingOptional'
import OnboardingDone from './OnboardingDone'
import OnboardingTour from './OnboardingTour'
import { OverlayPortal } from '../../components/ModalPortal'

// s95b (his ruling): when the opening sequence is finished the patient lands on Home.
const HOME_ROUTE = '/home'

// buildSectionPatch's data-section ids (stores/useOnboardingStore.js).
const SECTION = { identity: 2, insurance: 3, autofill: 4, allergies: 5, medications: 6 }

/**
 * OnboardingFrame — centered app shell, mirrors MobileLayout so the onboarding
 * flow is justified the same as every other screen. Phone: full-width column.
 * Tablet/desktop: 768px (max-w-3xl) white column centered on a slate-100
 * backdrop with a soft border/shadow. Defined at module scope (not inline) so
 * children never remount on re-render — that would drop input focus in the
 * identity form and restart screen-entry animations on every keystroke.
 * ★ PROD-1194: the frame pays the TOP inset only. Every step's Footer is the pinned
 *   BottomActionBar, which pays the bottom inset itself; paying it here as well put ~34px
 *   of white under the button on iPhone (and, once pinned, under the bar).
 */
function OnboardingFrame({ children, bg }) {
  return (
    <div className="screen-min-h bg-slate-100 flex flex-col" style={bg ? { background: bg } : undefined}>
      <div className="w-full max-w-3xl mx-auto screen-min-h bg-white flex flex-col relative lg:shadow-xl lg:border-x lg:border-slate-200"
        style={{ paddingTop: 'env(safe-area-inset-top)', ...(bg ? { background: bg } : {}) }}>
        {children}
      </div>
    </div>
  )
}

export default function OnboardingFlow() {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useI18n()
  const isAuthenticated = useUserStore((s) => s.isAuthenticated)
  const user = useUserStore((s) => s.user)
  const pendingUserId = useUserStore((s) => s.pendingUserId)
  const pendingProfile = useUserStore((s) => s.pendingProfile)
  const reloadProfile = useProfileStore((s) => s.loadProfile)
  const updateProfileLocal = useProfileStore((s) => s.updateProfile)
  const profile = useProfileStore((s) => s.profile)

  const currentStep = useOnboardingStore((s) => s.currentStep)
  const goToStep = useOnboardingStore((s) => s.goToStep)
  const nextStep = useOnboardingStore((s) => s.nextStep)
  const prevStep = useOnboardingStore((s) => s.prevStep)
  const buildPayload = useOnboardingStore((s) => s.buildPayload)
  const buildSectionPatch = useOnboardingStore((s) => s.buildSectionPatch)
  const buildEssentialsPayload = useOnboardingStore((s) => s.buildEssentialsPayload)
  const hydrateFromProfile = useOnboardingStore((s) => s.hydrateFromProfile)
  const reset = useOnboardingStore((s) => s.reset)
  const setSubmitting = useOnboardingStore((s) => s.setSubmitting)
  const setSubmitError = useOnboardingStore((s) => s.setSubmitError)

  // 'flow' (the three steps) | 'submitting' | 'done' (celebration) | 'profile' | 'tour'
  const [phase, setPhase] = useState('flow')
  const [profileStep, setProfileStep] = useState(0)
  const [error, setError] = useState(null)
  const prevStepRef = useRef(0)
  // s95: '/onboarding?run=profile' opens the health-profile run on its own for a patient who
  // finished sign-up (the before-share card sends them here). Back and Finish then return to
  // where they came from — never the celebration or the tour, which belong to sign-up.
  const profileOnlyRef = useRef(false)
  // s95b: sign-up can be left from any of its screens. Ask first; leaving signs out to
  // Welcome (signing in again resumes at the saved step).
  const [exitAsk, setExitAsk] = useState(false)
  // The first screen of the sequence greets ("Hi, I'm Orella."): the mobile step for an
  // Apple/Google account without a number, otherwise Security.
  const [phoneStepDone, setPhoneStepDone] = useState(false)

  // Hooks above every early return (v239.13.1, Rules of Hooks).
  const reduce = useReducedMotion()
  const stepVariants = reduce ? reducedVariants : screenVariants
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { prevStepRef.current = currentStep }, [currentStep])

  // The account the builders take a locked Email / Mobile from (TEST 3.01).
  const accountNow = () => {
    const u = useUserStore.getState()
    return u.user || u.pendingProfile
  }

  // Belt-and-suspenders auth check: a session, or a pending account mid-2FA-setup.
  useEffect(() => {
    if (!isAuthenticated && !pendingUserId) navigate('/welcome', { replace: true })
  }, [isAuthenticated, pendingUserId, navigate])

  // RESUME: an authenticated return mid-sign-up hydrates and jumps to the saved step.
  // Saved steps from the old nine-step flow (test data, D449) clamp to About you.
  const resumedRef = useRef(false)
  useEffect(() => {
    if (resumedRef.current) return
    if (isAuthenticated && !profile?.onboardingLoaded) return
    resumedRef.current = true
    if (isAuthenticated && profile?.onboardingCompleted === true) {
      // Sign-up is done: never run it again. Only the health-profile run opens here.
      if (new URLSearchParams(location.search).get('run') === 'profile') {
        profileOnlyRef.current = true
        hydrateFromProfile(profile)
        setProfileStep(0)
        setPhase('profile')
      } else {
        // PROD-1192 (L2a): a reload during the health-profile run after sign-up lands here. That
        // run is setup, so this is the patient's FIRST Home: restart the clocks once and consume
        // the marker, so nothing the run left behind (away stamp, an idle stamp 30+ min old) locks it.
        consumeProfileRunAtFirstHome()
        navigate('/home', { replace: true })
      }
      return
    }
    const saved = profile?.onboardingStep || 0
    if (isAuthenticated && profile?.onboardingCompleted === false && saved > 0) {
      hydrateFromProfile(profile)
      goToStep(Math.min(saved, 2))
    } else {
      goToStep(0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, profile?.onboardingLoaded])

  // Clear the in-memory store when leaving onboarding entirely.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => { reset() }, [])

  // The moment Security grants the session, record that sign-up has passed it, so a
  // quit after this point resumes at About you (never back at 2FA).
  const wasAuthRef = useRef(isAuthenticated)
  useEffect(() => {
    const justAuthed = !wasAuthRef.current && isAuthenticated
    wasAuthRef.current = isAuthenticated
    if (!justAuthed) return
    const cur = useProfileStore.getState().profile?.onboardingStep || 0
    if (cur >= 1) return
    profileAPI.update({ onboardingStep: 1 })
      .then(() => updateProfileLocal({ onboardingStep: 1 }))
      .catch((e) => console.warn('[ONBOARDING] post-2FA step mark failed (non-fatal):', e?.message || e))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated])

  // An Apple/Google account arrives with no mobile on it. Ask for one before Security,
  // so "text me a code" is a real choice there and visit reminders have a number.
  const accountPhone = (user && user.phone) || (pendingProfile && pendingProfile.phone) || ''
  const needsPhone = (isAuthenticated || !!pendingUserId) && !accountPhone && phase === 'flow' && currentStep === 0

  // About you: advance, then save identification (which also starts the server's
  // background insurance lookup) and the resume marker.
  const saveAboutAndNext = () => {
    nextStep()
    profileAPI.update({ ...buildSectionPatch(SECTION.identity, accountNow()), onboardingStep: 2 })
      .then(() => updateProfileLocal({ onboardingStep: 2 }))
      .catch((e) => console.warn('[ONBOARDING] About-you save failed (will retry at finish):', e?.message || e))
  }

  // Finish SIGN-UP. Writes identification + onboardingCompleted only.
  const finishSignUp = async () => {
    if (phase !== 'flow') return
    setError(null)
    setPhase('submitting')
    setSubmitting(true)
    try {
      await profileAPI.update(buildEssentialsPayload(accountNow()))
      updateProfileLocal({ onboardingCompleted: true, onboardingLoaded: true })
      // PROD-1192: setup is complete — clear anything setup left that could lock Home.
      useUserStore.getState().clearAwayForSetupEnd()
      // PROD-1192 (L2a): the health-profile run that follows is setup too, until the first Home.
      markProfileRunPending(useUserStore.getState().user?.id, Date.now())
      reloadProfile().catch(() => {})
      setPhase('done')
    } catch (err) {
      console.error('[OnboardingFlow] submit failed:', err)
      const dobKey = dobRefusalKey(err)
      const msg = dobKey
        ? t(dobKey, { age: ACCOUNT_HOLDER_MIN_AGE })
        : (err?.message || t('onboarding.saveFailed', 'Could not save your profile. Please try again.'))
      setError(msg)
      setSubmitError(msg)
      setPhase('flow')
    } finally {
      setSubmitting(false)
    }
  }

  // The optional health-profile run. Each screen saves its own section, marked complete,
  // the moment it is done — a patient who stops halfway keeps what they finished.
  // ★ PROD-696 follow-up (P4): `after` is the insurance step's unanswered PUT /profile (its blur
  //   check, sent in the same tap as Continue). Both PUTs write the whole insurance column, so this
  //   one leaves only after it is answered (measured: the other order dropped completed:true, and a
  //   failed step PUT left the coverage unchecked). The patch is built now, as the patient left it.
  // ★ PROD-1132: `opts.complete === false` ("I'll add my card later") saves the section NOT complete,
  //   so Continue's completed:true stamp does not satisfy the before-share check. Anything else: complete.
  const saveProfileSection = (section, after = null, opts = null) => {
    const patch = buildSectionPatch(section, accountNow(), { complete: opts?.complete !== false })
    Promise.resolve(after).catch(() => {}).then(() => profileAPI.update(patch))
      .then(() => {
        reloadProfile().catch(() => {})
        // Insurance on file → check coverage now (best-effort, as the old finish did).
        // ★ PROD-696 (P4): not when the insurance step already checked this same coverage in
        //   this run (measured at 53b5ce90: onboarding paid for 2 checks of one member ID, under
        //   a server limit of 3 in 24 h). Nothing is shown here (silent, as before).
        // ★ PROD-794: ...but the answer is STORED, the way the step stores its own, and while it is
        //   out the store says so (eligibilityRun). Measured at 5244fc2e: a member ID typed and
        //   Continue tapped with no blur was checked here, and Back paid a second check because
        //   the step had nothing to draw. Now Back draws it ("checking", then the result and
        //   Re-check); a soft fail stores nothing, so Back still checks once (review B-S2).
        const uid = useUserStore.getState().user?.id
        const onb = useOnboardingStore.getState()
        const ins = onb.insurance || {}
        const key = eligibilityKey(ins)
        if (section === SECTION.insurance && uid && ins.selfPay !== true && hasCarrierAndMemberId(ins) && !policyholderPending(ins) && onb.eligibilityCheckedKey !== key) { // PROD-822 A-1: no check without the policyholder's details
          onb.setEligibilityCheckedKey(key)
          let settled = () => {}
          const run = { key, put: Promise.resolve(), done: new Promise((resolve) => { settled = resolve }) }
          onb.setEligibilityRun(run)
          profileAPI.runPracticeEligibility(uid)
            .then((resp) => { storeEligibilityAnswer(useOnboardingStore.getState(), key, eligibilityAnswer(resp), run) }) // PROD-821: a stale run stores nothing
            .catch((e) => console.warn('[ONBOARDING] eligibility silent fail:', e?.message || e))
            .finally(() => {
              const st = useOnboardingStore.getState()
              if (st.eligibilityRun === run) st.setEligibilityRun(null)
              settled()
            })
        }
      })
      .catch((e) => console.warn(`[ONBOARDING] profile section ${section} save failed:`, e?.message || e))
  }
  const profileNext = (section, after = null, opts = null) => {
    if (section) saveProfileSection(section, after, opts)
    setProfileStep((n) => n + 1)
  }
  const leaveProfileRun = () => (window.history.length > 1 ? navigate(-1) : navigate('/home', { replace: true }))
  const profileBack = () => {
    if (profileStep > 0) return setProfileStep((n) => n - 1)
    return profileOnlyRef.current ? leaveProfileRun() : setPhase('done')
  }
  const finishProfile = async () => {
    // "More about you" (history, family, lifestyle) — only the parts the patient touched.
    const payload = buildPayload(accountNow())
    const optional = {}
    for (const k of ['medicalHistory', 'familyHistory', 'socialLifestyle']) {
      if (payload[k]) optional[k] = payload[k]
    }
    if (Object.keys(optional).length) {
      try { await profileAPI.update(optional) } catch (e) { console.warn('[ONBOARDING] optional save failed:', e?.message || e) }
    }
    reloadProfile().catch(() => {})
    if (profileOnlyRef.current) return leaveProfileRun()
    setPhase('tour')
  }

  const goHome = () => {
    reset()
    // PROD-1192: leaving setup for Home — the first Home render must not lock.
    useUserStore.getState().clearAwayForSetupEnd()
    clearProfileRunPending()
    navigate(HOME_ROUTE, { replace: true })
  }
  const askExit = () => setExitAsk(true)
  const leaveSignUp = () => {
    setExitAsk(false)
    reset()
    clearProfileRunPending()
    useUserStore.getState().logout()
    navigate('/welcome', { replace: true })
  }

  // ONE animated stage for every screen of the sequence — the canonical top-down drop
  // (config/transitions), so moving between steps, phases (celebration, profile run, tour)
  // and the mobile step all read the same, never a hard cut.
  let key = `s${currentStep}`
  let screen
  if (phase === 'tour') {
    key = 'tour'
    screen = <OnboardingTour onFinish={goHome} />
  } else if (phase === 'done') {
    key = 'done'
    screen = (
      <OnboardingDone
        onStartCase={() => setPhase('tour')}
        onSetupProfile={() => { setProfileStep(0); setPhase('profile') }}
      />
    )
  } else if (phase === 'profile') {
    const profileScreens = [
      <OnboardingInsurance key="insurance" onNext={(after, opts) => profileNext(SECTION.insurance, after, opts)} onBack={profileBack} />,
      <OnboardingAutoFill key="autofill" onNext={() => profileNext(null)} onBack={profileBack} />,
      <OnboardingAllergies key="allergies" onNext={() => profileNext(SECTION.allergies)} onBack={profileBack} />,
      <OnboardingMedications key="meds" onNext={() => profileNext(SECTION.medications)} onBack={profileBack} />,
      <OnboardingOptional key="optional" onNext={finishProfile} onBack={profileBack} onFinish={finishProfile} />,
    ]
    key = `p${profileStep}`
    screen = profileScreens[profileStep] || profileScreens[0]
  } else if (phase === 'submitting') {
    // Clean "Saving your profile..." state; flips to 'done' or back to 'flow' when the call resolves.
    key = 'submitting'
    screen = (
      <div
        className="h-full flex flex-col items-center justify-center flex-1 screen-min-h"
        style={{ background: '#fff', padding: '0 26px' }}
      >
        <div
          className="rounded-full flex items-center justify-center"
          style={{
            width: 76,
            height: 76,
            background: '#E9F8FF',
            border: '2px solid #46A8F9',
            marginBottom: 22,
            animation: 'sparkle-rotate 3s ease-in-out infinite',
          }}
        >
          <Sparkles size={30} color="#46A8F9" />
        </div>
        <p
          className="font-medium tracking-wide uppercase"
          style={{ fontSize: 13, letterSpacing: '0.04em', color: '#46A8F9', marginBottom: 12 }}
        >
          {t('onboarding.saving.almostThere')}
        </p>
        <h2
          className="font-medium text-center"
          style={{ fontSize: 22, lineHeight: 1.2, color: '#15354F', marginBottom: 8 }}
        >
          {t('onboarding.saving.savingProfile')}
        </h2>
        <p style={{ fontSize: 13.5, color: '#5B6E80', textAlign: 'center', maxWidth: 260 }}>
          {t('onboarding.saving.moment', 'A moment to put everything in place.')}
        </p>
        <style>{`
          @keyframes sparkle-rotate {
            0%,100% { transform: rotate(0deg) scale(1); opacity: 1; }
            50% { transform: rotate(180deg) scale(1.05); opacity: .75; }
          }
        `}</style>
      </div>
    )
  } else if (needsPhone) {
    key = 'phone'
    screen = <OnboardingPhone onDone={() => { setPhoneStepDone(true); goToStep(0) }} onExit={askExit} />
  } else {
    const screens = [
      <OnboardingSecurity key="security" onNext={nextStep} onBack={askExit} onExit={askExit} greet={!phoneStepDone} />,
      <OnboardingIdentity key="about" onNext={saveAboutAndNext} onBack={prevStep} onExit={askExit} />,
      <OnboardingAIConsent key="agreements" step={2} onNext={finishSignUp} onBack={prevStep} onExit={askExit} submitError={phase === 'flow' ? error : null} />,
    ]
    screen = screens[currentStep] || screens[0]
  }

  return (
    <OnboardingFrame>
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={key}
          variants={stepVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          style={{ width: '100%', display: 'flex', flexDirection: 'column', flex: 1 }}
        >
          {screen}
        </motion.div>
      </AnimatePresence>
      {/* PROD-1194 review M2: the finish error is no longer a fixed toast (bottom 80, over the pinned
          "Agree & Continue"): OnboardingAIConsent shows it in its bar's note slot (submitError). */}
      {exitAsk && (
        <OverlayPortal>
          <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center"
            style={{ background: 'rgba(0,0,0,0.45)' }} onClick={() => setExitAsk(false)}>
            <motion.div
              role="dialog" aria-modal="true"
              initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.22, ease: [0.12, 0.8, 0.16, 1] }}
              className="bg-white w-full max-w-[420px] rounded-t-3xl sm:rounded-3xl"
              style={{ padding: '24px 22px', paddingBottom: 'calc(22px + env(safe-area-inset-bottom, 0px))' }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 style={{ fontSize: 20, fontWeight: 700, color: '#15354F', margin: '0 0 8px' }}>
                {t('onboarding.exit.title', 'Leave sign-up?')}
              </h2>
              <p style={{ fontSize: 14.5, lineHeight: 1.5, color: '#4A5A6B', margin: '0 0 20px' }}>
                {t('onboarding.exit.body', "You'll be signed out. What you've finished is saved — sign in any time to pick up where you left off.")}
              </p>
              <button type="button" onClick={() => setExitAsk(false)}
                className="w-full font-bold text-white rounded-full active:scale-[0.98] transition-transform"
                style={{ height: 50, background: '#46A8F9', fontSize: 16, marginBottom: 10 }}>
                {t('onboarding.exit.stay', 'Keep going')}
              </button>
              <button type="button" onClick={leaveSignUp}
                className="w-full font-semibold rounded-full"
                style={{ height: 46, background: 'none', border: '1px solid #E2E8F0', color: '#15354F', fontSize: 15 }}>
                {t('onboarding.exit.leave', 'Leave and sign out')}
              </button>
            </motion.div>
          </div>
        </OverlayPortal>
      )}
    </OnboardingFrame>
  )
}
